import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { AssetType } from '../../../../models/defines';
import { FolderNameDialog } from '../../../../components/dialogs/folder-name/folder-name-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmComponent } from '../../../../components/dialogs/confirm/confirm.component';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsNotifierService } from '../../../../services/utils/analytics-notifier.service';
import { IUploadedFileMetadata } from '../../../../models/files/file.model';
import { WorkspaceAssetStateManagerService } from '../../../../services/workspace/state-management/associations/workspace-asset-state-manager.service';
import { WorkspaceAssetActionService } from '../../../../services/workspace/actions/workspace-asset-action.service';
import { AssetsLibraryServiceService } from './asset-library/assets-library-service.service';
import { ComponentId } from './assets-main/assets-main.component';
import { ILocalAsset } from '../../../../models/assets/assets.model';
import { AssetLocation, AssetViewMode, DEFAULT_ASSET_LOCATION } from './models/asset-browser.model';
import { AssetNavigationService } from './state/asset-navigation.service';
import { AssetSelectionService } from './state/asset-selection.service';
import { ConfigurationService } from '../../../../services/configuration.service';
import { IAssetBrowserPagination } from '../../../../services/workspace/api/associations/workspace-asset-request.types';
import { IAssetUsageError } from '../../../../services/workspace/state-management/associations/workspace-asset-state-manager.service';

function pageRangePaginatorIntl(): MatPaginatorIntl {
    const intl = new MatPaginatorIntl();
    intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
        if (length === 0) return '0 of 0';
        const totalPages = Math.ceil(length / pageSize);
        return `${page + 1} of ${totalPages}`;
    };
    return intl;
}

export interface IAssetData {
    assetRelativePath: string;
    assetId?: string;
    asset: ILocalAsset;
}
export interface IComponentUpdateWithAsset {
    component: ComponentId;
    asset: ILocalAsset;
}
@Component({
    selector: 'app-assets',
    templateUrl: './assets.component.html',
    styleUrls: ['./assets.component.scss'],
    providers: [{ provide: MatPaginatorIntl, useFactory: pageRangePaginatorIntl }],
})
export class AssetsComponent implements OnInit, OnDestroy {
    @Input({ alias: 'displayGenerate' }) displayGenerate = true;
    @Input({ alias: 'hosted' }) hosted = false;
    @Input({ alias: 'toDisplayLoading' }) toDisplayLoading = false;
    @Input({ alias: 'toDisplayFileDrop' }) toDisplayFileDrop = true;
    @Input({ alias: 'enableEditImage' }) enableEditImage = false;
    @Input({ alias: 'enableImageToVideo' }) enableImageToVideo = false;
    @Input() projectId: string | null = null;

    @Input({ alias: 'toShowSelectAsset' }) toShowSelectAsset = true;
    selectedAsset: ILocalAsset = null;

    @Output() selectedUrl = new EventEmitter<ILocalAsset>();
    @Output() updateComponent = new EventEmitter<IComponentUpdateWithAsset>();
    @Output() extendVideoEventEmitter = new EventEmitter<ILocalAsset>();
    cols: number;
    loadingData = false;
    gridByBreakpoint = {
        xl: 6,
        lg: 4,
        md: 3,
        sm: 2,
        xs: 1,
    };
    @Input({ alias: 'filterAssetBy' })
    filterAssetBy: AssetType;
    dialogType: AssetType;
    AssetType = AssetType;
    currentAsset: ILocalAsset;
    onDestroy$ = new Subject<boolean>();
    currentFolder: string;
    isRootFolder = false;
    toDisplayExtendVideoOption = false;
    desktopLocation: AssetLocation;
    desktopItems: ILocalAsset[] = [];
    desktopFolders: ILocalAsset[] = [];
    desktopSelectedIds: string[] = [];
    desktopLoading = false;
    desktopSidebarOpen = false;
    desktopPagination: IAssetBrowserPagination = { folders: null, assets: null };
    desktopError: string | null = null;
    desktopUsageError: IAssetUsageError | null = null;
    baseCdnUrl = '';
    @ViewChild('desktopUploadInput') desktopUploadInput?: ElementRef<HTMLInputElement>;
    private draggedAssetIds: string[] = [];
    private suppressNextActionSuccess = false;

    get desktopAccepts(): string {
        if (!this.hosted || !this.filterAssetBy) return '.svg,.png,.jpeg,.jpg,.gif,.mp4,.pdf';
        return this.filterAssetBy === AssetType.VIDEO ? '.mp4' : this.filterAssetBy === AssetType.IMAGE ? '.svg,.png,.jpeg,.jpg,.gif' : '.pdf';
    }

    get desktopSelectedAsset(): ILocalAsset | null {
        return this.desktopSelectedIds.length === 1 ? this.desktopItems.find((asset) => asset._id === this.desktopSelectedIds[0]) ?? null : null;
    }

    folders$ = this.wsAssetState.folders$;
    assets$ = this.wsAssetState.assets$;
    foldersPagination$ = this.wsAssetState.foldersPagination$;
    assetsPagination$ = this.wsAssetState.assetsPagination$;
    isFoldersLoading$ = this.wsAssetState.isFoldersLoading$;
    isAssetsLoading$ = this.wsAssetState.isAssetsLoading$;

    constructor(
        private breakpointObserver: BreakpointObserver,
        private dialog: MatDialog,
        private _snackBar: MatSnackBar,
        private analyticsNotifierService: AnalyticsNotifierService,
        private wsAssetState: WorkspaceAssetStateManagerService,
        public wsAssetActionService: WorkspaceAssetActionService,
        private assetsLibraryService: AssetsLibraryServiceService,
        public navigation: AssetNavigationService,
        private selection: AssetSelectionService,
        @Optional() private configurationService: ConfigurationService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data
    ) { this.baseCdnUrl = this.configurationService?.baseCdnUrl ?? ''; }

    // output to navigate to generate image (add the asset)
    @Input({ alias: 'selectAsset', required: false })
    set setImmediateAsset(asset: ILocalAsset) {
        if (!asset) {
            return;
        }
        this.selectFile(asset);
    }

    ngOnInit(): void {
        this.hosted = this.hosted || this.data?.hosted;

        this.filterAssetBy = this.data?.dialogType || this.filterAssetBy;

        this.wsAssetState.setFilterType(this.filterAssetBy ?? this.dialogType ?? null);
        this.subscribeToWorkspaceState();
        this.setExpendVideoAsync();
        this.initializeDesktopBrowser();

        this.breakpointObserver
            .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
                if (result.matches) {
                    if (result.breakpoints[Breakpoints.XSmall]) {
                        this.cols = this.gridByBreakpoint.xs;
                    }
                    if (result.breakpoints[Breakpoints.Small]) {
                        this.cols = this.gridByBreakpoint.sm;
                    }
                    if (result.breakpoints[Breakpoints.Medium]) {
                        this.cols = this.gridByBreakpoint.md;
                    }
                    if (result.breakpoints[Breakpoints.Large]) {
                        this.cols = this.gridByBreakpoint.lg;
                    }
                    if (result.breakpoints[Breakpoints.XLarge]) {
                        this.cols = this.gridByBreakpoint.xl;
                    }
                }
            });
    }
    private async setExpendVideoAsync() {
        await this.assetsLibraryService.setFeatureAccessForGenerateAsync();
        this.toDisplayExtendVideoOption = this.assetsLibraryService.toDisplayExtendVideoOption;
    }

    extendVideo(asset: ILocalAsset) {
        this.extendVideoEventEmitter.emit(asset);
    }

    uploadFileFinished(uploadedFileMetadata: IUploadedFileMetadata) {
        if (uploadedFileMetadata) {
            this.analyticsNotifierService.notifyEvent(`Asset Upload`, uploadedFileMetadata);
        }
        // this.wsAssetState.getAssetsForWSAsync();
    }

    selectFile(file: ILocalAsset) {
        this.currentAsset = file;
        console.log(file);
        if (!file) {
            return;
        }
        const assetData: IAssetData = {
            assetId: file._id,
            assetRelativePath: file.uploadPath,
            asset: file,
        };
        if (this.hosted) {
            this.selectedUrl.emit(file);
        }
        if (!this.hosted) {
            return;
        }
        if (this.selectedAsset === file) {
            // Deselect
            this.selectedAsset = null;
        } else {
            this.selectedAsset = file;
        }
    }

    moveFile(file: ILocalAsset, toFolder: string) {
        console.log('to folder', toFolder);

        this.dialog
            .open(ConfirmComponent, {
                minHeight: 'fit-content',
                data: {
                    message: `Are you sure you want to move ${file.displayName} to ${toFolder}?`,
                },
            })
            .afterClosed()
            .subscribe((response) => {
                if (response?.confirmed) {
                    console.log('Moving file');
                    this.wsAssetActionService.moveFileAsync(
                        { assetIds: file._id, toFolder: toFolder },
                        file.displayName,
                        file.folder
                    );
                }
            });
    }

    convertImageToVideo(file: ILocalAsset) {
        this.updateComponent.emit({ component: 'generate-video', asset: file });
    }

    editImage(file: ILocalAsset) {
        this.updateComponent.emit({ component: 'generate-image', asset: file });
    }

    renameFile(file: ILocalAsset) {
        const oldName = file?.displayName + '';
        this.dialog
            .open(FolderNameDialog, {
                minHeight: 'fit-content',
                data: { isNew: false, oldName: oldName, fileMode: true },
            })
            .afterClosed()
            .subscribe((response) => {
                if (response?.newName) {
                    console.log('Sending file name change');
                    this.wsAssetActionService.renameFileAsync(
                        {
                            newName: response.newName,
                            assetId: file._id,
                        },
                        oldName
                    );
                }
            });
    }

    deleteFile(file: ILocalAsset) {
        this.dialog
            .open(ConfirmComponent, {
                minHeight: 'fit-content',
                data: {
                    message: `Are you sure you want to delete ${file.displayName}?`,
                },
            })
            .afterClosed()
            .subscribe((response) => {
                if (response?.confirmed) {
                    console.log('Deleting folder');
                    this.wsAssetActionService.removeAssetAsync(file._id, file.displayName);
                }
            });
    }

    // renameFolder(folderName: string, folderId: string) {
    //     this.dialog
    //         .open(FolderNameDialog, {
    //             minHeight: 'fit-content',
    //             data: { isNew: false, oldName: folderName, fileMode: false },
    //         })
    //         .afterClosed()
    //         .subscribe((response) => {
    //             if (response?.newName) {
    //                 console.log('Sending new folder');
    //                 this.wsAssetActionService.renameFolderAsync(folderName, {
    //                     newName: response.newName,
    //                     assetId: folderId,
    //                 });
    //             }
    //         });
    // }

    selectFolder(folderName: string) {
        if (!folderName) {
            return;
        }
        this.assetsLibraryService.setCurrentFolder(folderName);
    }

    openNewFolderDialog() {
        this.dialog
            .open(FolderNameDialog, {
                minHeight: 'fit-content',
                data: { isNew: true, oldName: null, fileMode: false },
            })
            .afterClosed()
            .subscribe((response) => {
                //{newName: 'gaegaa', oldName: '', isNew: true}
                console.log(response);
                if (response?.newName) {
                    console.log('Sending new folder');
                    this.wsAssetActionService.createFolderAsync(response?.newName);
                }
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    private subscribeToWorkspaceState() {
        this.wsAssetState.currentFolderName$.pipe(takeUntil(this.onDestroy$)).subscribe((folderName) => {
            this.currentFolder = folderName;
            this.isRootFolder = this.currentFolder === '/';
        });
        this.wsAssetState.isLoading$.pipe(takeUntil(this.onDestroy$)).subscribe((isLoading) => {
            this.loadingData = isLoading;
        });
        this.wsAssetActionService.successMessage$.pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            if (!message) {
                return;
            }
            if (this.suppressNextActionSuccess) {
                return;
            }
            this._snackBar.open(message, 'Dismiss', { duration: 3000 });
        });
        this.wsAssetActionService.actionError$.pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            if (!message) {
                return;
            }
            this._snackBar.open(message, 'Dismiss', { duration: 3000 });
        });
    }

    onFolderPageChange(page: number) {
        this.wsAssetState.setFolderPage(page);
    }
    onAssetPageChange(page: number) {
        this.wsAssetState.setAssetPage(page);
    }
    exitFolder() {
        this.assetsLibraryService.setCurrentFolder(null);
    }

    navigateDesktop(location: AssetLocation): void {
        this.navigation.go(location);
    }

    setDesktopViewMode(viewMode: AssetViewMode): void {
        this.navigation.go({ ...this.desktopLocation, viewMode });
    }

    selectDesktopAsset(asset: ILocalAsset, event?: MouseEvent): void {
        if (event?.shiftKey) {
            this.selection.selectRange([...this.desktopFolders, ...this.desktopItems].map((item) => item._id), asset._id);
        } else if (event?.ctrlKey || event?.metaKey) {
            this.selection.toggle(asset._id);
        } else {
            this.selection.selectOnly(asset._id);
        }
        this.selectedAsset = asset;
    }

    openDesktopAsset(asset: ILocalAsset): void {
        if (asset.type === AssetType.FOLDER) {
            this.navigation.go({ ...this.desktopLocation, folderPath: [...this.desktopLocation.folderPath, asset.displayName] });
            return;
        }
        if (this.hosted && this.toShowSelectAsset) {
            this.selectFile(asset);
            return;
        }
        this.previewDesktopAsset(asset);
    }

    beginAssetDrag(asset: ILocalAsset): void {
        this.draggedAssetIds = this.desktopSelectedIds.includes(asset._id) ? [...this.desktopSelectedIds] : [asset._id];
        if (this.draggedAssetIds.length === 1) {
            this.selection.selectOnly(asset._id);
        }
    }

    moveDesktopAssets(folderName: string): void {
        if (!this.draggedAssetIds.length) {
            this.draggedAssetIds = [...this.desktopSelectedIds];
        }
        const assets = this.desktopItems.filter((asset) => this.draggedAssetIds.includes(asset._id));
        assets.forEach((asset) =>
            void this.wsAssetActionService.moveFileAsync(
                { assetIds: asset._id, toFolder: folderName },
                asset.displayName,
                asset.folder
            )
        );
        this.draggedAssetIds = [];
    }

    addDraggedAssetsToProject(): void {
        const assetIds = this.draggedAssetIds.length ? this.draggedAssetIds : this.desktopSelectedIds;
        if (this.desktopLocation.projectId && assetIds.length) {
            void this.wsAssetActionService.addToProjectAsync(this.desktopLocation.projectId, assetIds);
        }
        this.draggedAssetIds = [];
    }

    renameDesktopFolder(folder: ILocalAsset): void {
        this.dialog.open(FolderNameDialog, { minHeight: 'fit-content', data: { isNew: false, oldName: folder.displayName, fileMode: false } })
            .afterClosed().subscribe((response) => response?.newName && void this.wsAssetActionService.renameFolderAsync(folder._id, response.newName));
    }

    deleteDesktopFolder(folder: ILocalAsset): void {
        this.dialog.open(ConfirmComponent, { minHeight: 'fit-content', data: { message: `Move ${folder.displayName}'s contents to the workspace root before deleting the folder? Choose Cancel to select Trash instead.` } })
            .afterClosed().subscribe((response) => {
                if (response?.confirmed) {
                    void this.deleteDesktopFolderWithChildren(folder, 'move');
                    return;
                }
                this.dialog.open(ConfirmComponent, { minHeight: 'fit-content', data: { message: `Move ${folder.displayName}'s contents to Trash?` } })
                    .afterClosed().subscribe((trashResponse) => trashResponse?.confirmed && void this.deleteDesktopFolderWithChildren(folder, 'trash'));
            });
    }

    handleDesktopSelectionAction(action: 'rename' | 'download' | 'removeFromProject' | 'trash' | 'restore' | 'permanent' | 'edit' | 'createVideo' | 'extend'): void {
        if (action === 'trash') {
            this.trashDesktopSelection();
        }
        if (action === 'removeFromProject') {
            void this.wsAssetActionService.removeFromProjectAsync(this.desktopSelectedIds);
        }
        if (action === 'download') {
            this.downloadDesktopSelection();
        }
        if (action === 'restore') {
            void this.restoreDesktopSelection();
        }
        if (action === 'permanent') {
            void this.wsAssetActionService.permanentlyRemoveAsync(this.desktopSelectedIds);
        }
        if (action === 'edit' && this.desktopSelectedAsset) this.editImage(this.desktopSelectedAsset);
        if (action === 'createVideo' && this.desktopSelectedAsset) this.convertImageToVideo(this.desktopSelectedAsset);
        if (action === 'extend' && this.desktopSelectedAsset) this.extendVideo(this.desktopSelectedAsset);
        if (action === 'rename' && this.desktopSelectedIds.length === 1) {
            const selectedAsset = this.desktopItems.find((item) => item._id === this.desktopSelectedIds[0]);
            if (selectedAsset) {
                this.renameFile(selectedAsset);
            }
        }
    }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: KeyboardEvent): void {
        if (this.isEditableTarget(event.target) || this.isInteractiveTarget(event.target)) {
            return;
        }
        if (event.key === 'Delete' && this.desktopSelectedIds.length) {
            event.preventDefault();
            void this.trashDesktopSelection();
            return;
        }
        if (event.key === 'F2' && this.desktopSelectedIds.length === 1) {
            const selectedAsset = this.desktopItems.find((item) => item._id === this.desktopSelectedIds[0]);
            if (selectedAsset) {
                event.preventDefault();
                this.renameFile(selectedAsset);
            }
            return;
        }
        if (event.key === 'Escape') {
            this.selection.clear();
            return;
        }
    }

    private initializeDesktopBrowser(): void {
        this.navigation.location$.pipe(takeUntil(this.onDestroy$)).subscribe((location) => {
            this.desktopLocation = location;
            this.selection.clear();
            void this.wsAssetState.loadLocationAsync(location);
        });
        this.navigation.reset({
            ...DEFAULT_ASSET_LOCATION,
            scope: this.projectId ? 'project' : 'workspace',
            projectId: this.projectId,
            collection: 'all',
            filters: { type: this.filterAssetBy ?? null },
        });
        this.wsAssetState.workspaceReset$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.selectedAsset = null;
            this.currentAsset = null;
            this.draggedAssetIds = [];
            this.navigation.reset({
                ...DEFAULT_ASSET_LOCATION,
                filters: { type: this.hosted ? this.filterAssetBy ?? null : null },
            });
        });
        this.wsAssetState.items$.pipe(takeUntil(this.onDestroy$)).subscribe((items) => (this.desktopItems = items ?? []));
        this.wsAssetState.folders$.pipe(takeUntil(this.onDestroy$)).subscribe((folders) => (this.desktopFolders = folders ?? []));
        this.wsAssetState.loading$.pipe(takeUntil(this.onDestroy$)).subscribe((loading) => (this.desktopLoading = loading));
        this.wsAssetState.pagination$.pipe(takeUntil(this.onDestroy$)).subscribe((pagination) => (this.desktopPagination = pagination));
        this.wsAssetState.error$.pipe(takeUntil(this.onDestroy$)).subscribe((error) => (this.desktopError = error));
        this.wsAssetState.usageError$.pipe(takeUntil(this.onDestroy$)).subscribe((error) => (this.desktopUsageError = error));
        this.selection.selectedIds$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((selectedIds) => (this.desktopSelectedIds = [...selectedIds]));
    }

    async trashDesktopSelection(): Promise<void> {
        const assetIds = [...this.desktopSelectedIds];
        if (!assetIds.length) {
            return;
        }
        this.suppressNextActionSuccess = true;
        const trashed = await this.wsAssetActionService.trashAsync(assetIds);
        this.suppressNextActionSuccess = false;
        if (!trashed) {
            return;
        }
        this._snackBar
            .open('Assets moved to Trash', 'Undo', { duration: 5000 })
            .onAction()
            .subscribe(() => void this.restoreDesktopSelection(assetIds));
    }

    openDesktopUpload(): void { this.desktopUploadInput?.nativeElement.click(); }

    onDesktopUpload(event: Event): void {
        const files = (event.target as HTMLInputElement).files;
        if (!files?.length) return;
        const formData = new FormData();
        Array.from(files).forEach((file) => formData.append('files[]', file));
        const folder = this.desktopLocation.folderPath[this.desktopLocation.folderPath.length - 1] ?? '/';
        if (Array.from(files).some((file) => !this.isAcceptedDesktopFile(file))) {
            this._snackBar.open('One or more files do not match this picker’s allowed type.', 'Dismiss', { duration: 4000 });
            return;
        }
        void this.wsAssetActionService.uploadAssetAsync(formData, folder, this.desktopLocation.projectId);
        (event.target as HTMLInputElement).value = '';
    }

    updateDesktopLocation(patch: Partial<AssetLocation>): void { this.navigation.go({ ...this.desktopLocation, ...patch }); }
    setDesktopFilter(filter: AssetType | null): void { this.updateDesktopLocation({ filters: { type: this.hosted && this.filterAssetBy ? this.filterAssetBy : filter } }); }
    navigateDesktopCrumb(index: number): void { this.navigation.go({ ...this.desktopLocation, folderPath: this.desktopLocation.folderPath.slice(0, Math.max(0, index)) }); }
    loadMoreDesktopAssets(): void { this.wsAssetState.loadMoreAssets(); }
    loadMoreDesktopFolders(): void { this.wsAssetState.loadMoreFolders(); }

    private async restoreDesktopSelection(assetIds = [...this.desktopSelectedIds]): Promise<void> {
        if (await this.wsAssetActionService.restoreAsync(assetIds)) {
            this.selection.clear();
            await this.wsAssetState.loadAssetsAsync();
        }
    }

    private async deleteDesktopFolderWithChildren(folder: ILocalAsset, children: 'move' | 'trash'): Promise<void> {
        if (await this.wsAssetActionService.deleteFolderAsync(folder._id, children)) {
            await this.wsAssetState.loadAssetsAsync();
        }
    }

    private async downloadDesktopSelection(): Promise<void> {
        for (const asset of this.desktopItems.filter((item) => this.desktopSelectedIds.includes(item._id))) {
            try {
                const path = asset.uploadPath ?? '';
                const url = /^https?:\/\//i.test(path) ? path : `${this.baseCdnUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error('Download failed');
                const objectUrl = URL.createObjectURL(await response.blob());
                const anchor = document.createElement('a');
                anchor.href = objectUrl;
                anchor.download = asset.displayName;
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                URL.revokeObjectURL(objectUrl);
            } catch {
                this._snackBar.open(`Could not download ${asset.displayName}.`, 'Dismiss', { duration: 4000 });
            }
        }
    }

    private previewDesktopAsset(asset: ILocalAsset): void {
        const path = asset.uploadPath ?? '';
        const url = /^https?:\/\//i.test(path) ? path : `${this.baseCdnUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
        window.open(url, '_blank', 'noopener');
    }

    private isEditableTarget(target: EventTarget | null): boolean {
        const element = target as HTMLElement | null;
        return !!element && (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName));
    }

    usageLocation(usage: IAssetUsageError['usages'][number]): string {
        const location = [usage.sceneId && `scene ${usage.sceneId}`, usage.takeId && `take ${usage.takeId}`, usage.positionId && `position ${usage.positionId}`].filter(Boolean);
        return `${usage.kind}${location.length ? ` — ${location.join(', ')}` : ''}`;
    }

    private isInteractiveTarget(target: EventTarget | null): boolean {
        const element = target as HTMLElement | null;
        return !!element?.closest('button,a,input,select,textarea,[role="button"],[contenteditable="true"]');
    }

    private isAcceptedDesktopFile(file: File): boolean {
        if (!this.hosted || !this.filterAssetBy) return true;
        const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
        return this.filterAssetBy === AssetType.VIDEO ? extension === '.mp4' : this.filterAssetBy === AssetType.IMAGE ? ['.svg', '.png', '.jpeg', '.jpg', '.gif'].includes(extension) : extension === '.pdf';
    }
}
